import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, Chip, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from '@mui/material';
import { Close, Navigation, Speed, Battery3Bar, SignalCellularAlt } from '@mui/icons-material';
import { InvokeApi } from '../services/ApiService';
import { Config } from '../config/Config';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

/**
 * Vehicle Path Map Component
 * Shows interactive map with vehicle route, journey points, and statistics
 */

const VehiclePathMap = ({ vehicleId, date, onClose }) => {
  const [coordinates, setCoordinates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [journeys, setJourneys] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [map, setMap] = useState(null);
  const [polyline, setPolyline] = useState(null);
  const mapRef = React.useRef(null);

  // Fetch route coordinates
  useEffect(() => {
    const fetchRoute = async () => {
      if (!vehicleId || !date) return;
      
      setLoading(true);
      try {
        const res = await InvokeApi(Config.base_url + '/device/vehicle-path-daily', {
          vehicle_id: vehicleId,
          date: date,
        });
        
        if (res?.code === '200') {
          // Get all coordinates from the response
          const coords = res.all_coordinates || [];
          setCoordinates(coords);
          setJourneys(res.journeys || []);
        }
      } catch (err) {
        console.error('Failed to fetch route:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoute();
  }, [vehicleId, date]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) return;

    const newMap = L.map(mapRef.current).setView([28.6139, 77.2090], 11);
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(newMap);

    setMap(newMap);

    return () => {
      newMap.remove();
    };
  }, []);

  // Draw route polyline
  useEffect(() => {
    if (!map || coordinates.length === 0) return;

    // Remove old polyline
    if (polyline) {
      map.removeLayer(polyline);
    }

    // Create coordinates array for polyline
    const latLngs = coordinates.map(coord => [coord.lat, coord.lng]);
    
    // Draw polyline
    const newPolyline = L.polyline(latLngs, {
      color: '#1a237e',
      weight: 3,
      opacity: 0.8,
      smoothFactor: 1,
    }).addTo(map);

    setPolyline(newPolyline);

    // Fit bounds
    const group = new L.featureGroup([newPolyline]);
    map.fitBounds(group.getBounds().pad(0.1));

    // Add markers for start and end points
    if (latLngs.length > 0) {
      // Start marker (green)
      L.circleMarker(latLngs[0], {
        radius: 8,
        fillColor: '#4CAF50',
        color: '#fff',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.8,
      })
        .addTo(map)
        .bindPopup(`<b>Start</b><br>Speed: ${coordinates[0].speed} km/h`);

      // End marker (red)
      L.circleMarker(latLngs[latLngs.length - 1], {
        radius: 8,
        fillColor: '#f44336',
        color: '#fff',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.8,
      })
        .addTo(map)
        .bindPopup(
          `<b>End</b><br>Speed: ${coordinates[coordinates.length - 1].speed} km/h`
        );
    }

    // Add journey start/end points
    journeys.forEach((journey, idx) => {
      // Journey start
      L.circleMarker([journey.start_lat, journey.start_lon], {
        radius: 6,
        fillColor: '#FF9800',
        color: '#fff',
        weight: 1,
        opacity: 0.8,
        fillOpacity: 0.7,
      })
        .addTo(map)
        .bindPopup(
          `<b>Journey ${idx + 1} Start</b><br>Time: ${new Date(journey.start_time).toLocaleTimeString()}<br>Distance: ${journey.distance_km} km`
        );
    });
  }, [map, coordinates, journeys, polyline]);

  // Handle point click
  const handlePointClick = (point) => {
    setSelectedPoint(point);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 600 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      {/* Map Container */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden', flex: 1 }}>
        <Box
          ref={mapRef}
          sx={{
            width: '100%',
            height: 500,
            borderRadius: 2,
          }}
        />
      </Card>

      {/* Statistics */}
      <Grid container spacing={2}>
        {[
          {
            label: 'Total Points',
            value: coordinates.length,
            icon: '📍',
            color: '#1a237e',
          },
          {
            label: 'Journeys',
            value: journeys.length,
            icon: '🛣️',
            color: '#ff9800',
          },
          {
            label: 'Total Distance',
            value: `${journeys.reduce((sum, j) => sum + j.distance_km, 0).toFixed(2)} km`,
            icon: '📏',
            color: '#43a047',
          },
          {
            label: 'Max Speed',
            value: `${Math.max(...coordinates.map(c => c.speed), 0)} km/h`,
            icon: '🚀',
            color: '#e53935',
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ borderRadius: 1, background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`, border: `2px solid ${stat.color}` }}>
              <CardContent sx={{ py: 1, px: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {stat.icon} {stat.label}
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} color={stat.color} sx={{ mt: 0.5 }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Journeys List */}
      {journeys.length > 0 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1a237e' }}>
              Journeys ({journeys.length})
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['#', 'Start Time', 'Distance', 'Duration', 'Avg Speed', 'Max Speed'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: '#1a237e' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {journeys.map((journey, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell fontSize={11}>{idx + 1}</TableCell>
                      <TableCell fontSize={11}>{new Date(journey.start_time).toLocaleTimeString()}</TableCell>
                      <TableCell>
                        <Chip label={`${journey.distance_km} km`} size="small" />
                      </TableCell>
                      <TableCell fontSize={11}>{journey.duration_minutes} min</TableCell>
                      <TableCell fontSize={11}>{journey.avg_speed_kmh} km/h</TableCell>
                      <TableCell>
                        <Chip label={`${journey.max_speed_kmh} km/h`} size="small" color={journey.max_speed_kmh > 60 ? 'error' : 'default'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Selected Point Details */}
      {selectedPoint && (
        <Dialog open={true} onClose={() => setSelectedPoint(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Navigation sx={{ color: '#1a237e' }} />
              <Typography fontWeight={700}>Point Details</Typography>
            </Box>
            <IconButton onClick={() => setSelectedPoint(null)} size="small">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Location
                </Typography>
                <Typography fontFamily="monospace" fontSize={12} fontWeight={600}>
                  {selectedPoint.lat.toFixed(6)}, {selectedPoint.lng.toFixed(6)}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Chip icon={<Speed />} label={`${selectedPoint.speed} km/h`} size="small" />
                </Box>
                <Box>
                  <Chip icon={<Battery3Bar />} label={`Battery`} size="small" />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Time
                </Typography>
                <Typography fontSize={12}>
                  {new Date(selectedPoint.time).toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Heading
                </Typography>
                <Typography fontSize={12}>{selectedPoint.heading}°</Typography>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default VehiclePathMap;
